import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, IndianRupee } from 'lucide-react';
import Navbar from '../components/Navbar';
import api from '../utils/api';
import { toast } from 'sonner';

const Rooms = () => {
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  const checkIn = searchParams.get('checkIn');
  const checkOut = searchParams.get('checkOut');
  const guests = parseInt(searchParams.get('guests') || '2');
  
  // Only consider we have dates if BOTH checkIn AND checkOut are present and valid
  const hasDates = Boolean(checkIn && checkOut && checkIn.length > 0 && checkOut.length > 0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [checkIn, checkOut]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rooms');
      const roomsData = response.data;
      setRooms(roomsData);

      // CRITICAL: Only check availability if we have VALID dates
      if (hasDates) {
        const availabilityMap = {};

        await Promise.all(
          roomsData.map(async (room) => {
            try {
              const res = await api.get(`/rooms/${room.id}/availability`, {
                params: { check_in: checkIn, check_out: checkOut },
              });
              availabilityMap[room.id] = res.data.available;
            } catch (err) {
              console.error(`Error checking availability for room ${room.id}:`, err);
              // On error, assume available (don't block user)
              availabilityMap[room.id] = true;
            }
          })
        );

        setAvailability(availabilityMap);
      } else {
        // No dates selected - clear availability map, all rooms available
        setAvailability({});
      }
    } catch (error) {
      console.error('Failed to load rooms:', error);
      toast.error('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <Navbar scrolled={scrolled} />

      <div className="pt-32 pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="overline text-[#8F9E8B] mb-4">OUR ROOMS</p>
            <h1
              className="text-5xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Choose Your Sanctuary
            </h1>
            <p className="text-lg" style={{ color: '#64748B' }}>
              Each room is thoughtfully designed for comfort and tranquility
            </p>
            {hasDates && (
              <p className="text-sm mt-4" style={{ color: '#8F9E8B' }}>
                Showing availability for {checkIn} to {checkOut}
              </p>
            )}
          </motion.div>

          {loading ? (
            <div className="text-center py-20">
              <p style={{ color: '#64748B' }}>Loading rooms...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rooms.map((room, index) => {
                // CRITICAL: Only check availability status if dates are selected
                // If no dates, always show as available (isAvailable = true)
                const isAvailable = hasDates ? (availability[room.id] ?? true) : true;
                const showSoldOut = hasDates && !isAvailable;

                return (
                  <motion.div
                    key={room.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`room-card card-hover ${showSoldOut ? 'opacity-50' : ''}`}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={room.image_url}
                        alt={room.name}
                        className="w-full h-64 object-cover"
                      />

                      {showSoldOut && (
                        <div
                          className="absolute top-4 right-4 px-4 py-2 rounded-full text-sm font-medium"
                          style={{ background: '#C67D63', color: 'white' }}
                        >
                          Sold Out
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <h3
                        className="text-2xl font-bold mb-2"
                        style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
                      >
                        {room.name}
                      </h3>

                      <p className="mb-4" style={{ color: '#64748B' }}>
                        {room.description}
                      </p>

                      <div className="flex items-center gap-4 mb-4">
                        <Users size={18} />
                        <span>Up to {room.max_guests} guests</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <IndianRupee size={20} />
                          <span className="text-2xl font-bold mono">
                            {room.price_per_night}
                          </span>
                          <span className="text-sm ml-1" style={{ color: '#64748B' }}>/night</span>
                        </div>

                        {!showSoldOut ? (
                          <Link
                            to={`/booking/${room.id}${
                              hasDates
                                ? `?checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}`
                                : ''
                            }`}
                          >
                            <button className="btn-primary">Book Now</button>
                          </Link>
                        ) : (
                          <button className="btn-primary opacity-50 cursor-not-allowed" disabled>
                            Sold Out
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Rooms;
